import { gql } from "lib/apolloClient";
import {
  plannerFieldFragment,
  staffDetailUnitPlanFeedFragment,
  resourceNodeFragment,
  unitPlanResourceLibraryFragment,
  plannerFieldsOfNodeFragment,
  courseDetailUnitPlanFeedFragment,
  plannerOrganizationResourceNodeFragment,
  templateFieldConnectionFragment,
  unitPlanFragment,
  unitPlanFeedFragment,
  organizationUnitPlanFeedFragment,
  plannerElementNodeSetFragment,
  unitPlanGradeFragment,
} from "./UnitPlanFragments";
import {
  gradeFragment,
  subjectGroupFragment,
  courseFragment,
  subjectFragment,
} from "modules/CommonFragments";

export const getSubjectGroupsQuery = gql`
  query getSubjectGroups(
    $organizationId: ID!
    $filters: SubjectGroupFilterInput
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        subjectGroups(filters: $filters) {
          ...subjectGroupBasicDetailsItem
          subjects {
            ...subjectItem
            recommendedPhase {
              gradeId
              phaseId
            }
          }
          globalSubjectGroup {
            id
            name
            constants
          }
        }
      }
    }
  }
  ${subjectGroupFragment.subjectGroupBasicDetails}
  ${subjectFragment.subjectBenchmarkDetails}
`;

export const getFlatUnitPlanFeedQuery = gql`
  query getFlatUnitPlanFeed(
    $id: ID!
    $after: String
    $filters: UnitPlansV2FeedFilters
    $showCollaborators: Boolean!
    $first: Int
    $orderBy: UnitPlanOrderByEnum
    $orderByDirection: OrderByDirectionEnum
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
    $exceptionalCollaboratorsFilter: ExceptionalCollaboratorUserFilter
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseFlatUnitPlanFeedItem
      }
    }
  }
  ${courseDetailUnitPlanFeedFragment.courseFlatUnitPlanFeed}
`;

export const getFlatUnitPlanTitleQuery = gql`
  query getFlatUnitPlanTitle(
    $id: ID!
    $after: String
    $filters: UnitPlansV2FeedFilters
    $orderBy: UnitPlanOrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseFlatUnitPlanTitleItem
      }
    }
  }
  ${courseDetailUnitPlanFeedFragment.courseFlatUnitPlanTitle}
`;

export const getUnitPlanFeedQuery = gql`
  query getUnitPlanFeed(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UnitPlanFilter
    $groupBy: UNIT_PLAN_FEED_GROUPING_ENUM
    $showCollaborators: Boolean!
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on Course {
        ...courseUnitPlanFeedItem
      }
      ... on Staff {
        ...staffDetailUnitPlanFeedItem
      }
    }
  }
  ${staffDetailUnitPlanFeedFragment.staffDetailUnitPlanFeed}
  ${courseDetailUnitPlanFeedFragment.courseUnitPlanFeed}
`;

export const getNodeUnitPlanFeedQuery = gql`
  query getNodeUnitPlanFeed(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: UnitPlansV2FeedFilters!
    $after: String
    $first: Int
    $showCollaborators: Boolean! = false
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on Organization {
        unitPlansV2(first: $first, after: $after, filters: $filters) {
          totalCount
          edges {
            node {
              ...unitPlanItem
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${unitPlanFeedFragment.unitPlanFeed}
`;

// export const getUnitPlanFeedCountQuery = gql`
//   query getUnitPlanFeedCount($ids: [ID!], $grades: [String!]) {
//     platform {
//       staff(ids: $ids) {
//         ...staffUnitPlanFeedCountItem
//       }
//     }
//   }
//   ${staffDetailUnitPlanFeedFragment.staffUnitPlanFeedCount}
// `;

export const getPlannerResourcesOfNodeQuery = gql`
  query getPlannerResourcesOfNode($id: ID!, $type: ENTITY_TYPE_ENUM!) {
    node(id: $id, type: $type) {
      id
      ... on UnitPlan {
        ...unitPlanNodeItem
      }
      ... on Assessment {
        ...assessmentNodeItem
      }
      ... on LearningEngagement {
        ...leNodeItem
      }
    }
  }

  ${resourceNodeFragment.le}
  ${resourceNodeFragment.assessment}
  ${resourceNodeFragment.unitPlan}
`;

export const getPlannerOrganizationResourcesOfNodeQuery = gql`
  query getPlannerOrganizationResourcesOfNode(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $first: Int
    $filters: OrganizationResourceFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on UnitPlan {
        ...unitPlanOrganizationResourceNodeItem
      }
      ... on Assessment {
        ...assessmentOrganizationResourceNodeItem
      }
    }
  }

  ${plannerOrganizationResourceNodeFragment.assessmentOrganizationResourceNode}
  ${plannerOrganizationResourceNodeFragment.unitPlanOrganizationResourceNode}
`;

export const getPlannerFieldsOfNodeQuery = gql`
  query getPlannerFieldsOfNode(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $uids: [String!]
    $responseFilter: ResponseFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on UnitPlan {
        ...unitPlanNodeItem
      }
      ... on Assessment {
        ...assessmentNodeItem
      }
      ... on LearningEngagement {
        ...leNodeItem
      }
      ... on Project {
        ...projectNodeItem
      }
    }
  }
  ${plannerFieldsOfNodeFragment.unitPlan}
  ${plannerFieldsOfNodeFragment.le}
  ${plannerFieldsOfNodeFragment.assessment}
  ${plannerFieldsOfNodeFragment.project}
`;

export const getUnitPlanResourceLibraryQuery = gql`
  query getUnitPlanResourceLibrary($id: ID!, $filters: resourceLibraryFilter) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        ...unitPlanResourceLibraryItem
      }
    }
  }
  ${unitPlanResourceLibraryFragment.unitPlanResourceLibrary}
`;

export const getUnitPlansQuery = gql`
  query getUnitPlans(
    $ids: [ID!]
    $uids: [String!]
    $filters: UnitInsightFilters
    $pypUnitInsightFilters: PYPUnitInsightFilters
    $fetchUnitInsights: Boolean!
    $fetchPYPUnitInsights: Boolean!
  ) {
    multiNode(ids: $ids, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        id
        title {
          ...unitplanFieldBasicDetailsItem
        }
        image {
          ...unitplanFieldBasicDetailsItem
        }
        fields(uids: $uids) {
          ...unitplanFieldBasicDetailsItem
        }
        unitInsights(filters: $filters) @include(if: $fetchUnitInsights) {
          assessments {
            edges {
              node {
                id
              }
            }
            totalCount
          }
        }
        pypUnitInsight(filters: $pypUnitInsightFilters)
          @include(if: $fetchPYPUnitInsights) {
          ... on ATLUnitInsight {
            elementAssessmentInsight {
              assessments {
                totalCount
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
          ... on BenchmarkUnitInsight {
            elementAssessmentInsight {
              assessments {
                totalCount
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
          ... on LPAUnitInsight {
            elementAssessmentInsight {
              assessments {
                totalCount
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
          ... on ConceptUnitInsight {
            elementAssessmentInsight {
              assessments {
                totalCount
                edges {
                  node {
                    id
                  }
                }
              }
            }
          }
        }
      }
    }
  }
  ${plannerFieldFragment.unitplanFieldBasicDetails}
`;

export const getAssessmentsQuery = gql`
  query getAssessments($ids: [ID!]) {
    multiNode(ids: $ids, type: ASSESSMENT) {
      ... on Assessment {
        id
        type
        title {
          ...assessmentFieldItem
        }
        image {
          ...assessmentFieldItem
        }
        assessmentType {
          ...assessmentFieldItem
        }
      }
    }
  }
  ${plannerFieldFragment.assessmentFieldItem}
`;

export const getPlannerFieldResponsesQuery = gql`
  query getPlannerFieldResponses(
    $id: ID!
    $uids: [String!]
    $type: ENTITY_TYPE_ENUM!
    $responseFilter: ResponseFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on UnitPlan {
        id
        fields(uids: $uids) {
          ...unitPlanFieldItem
        }
      }
    }
  }
  ${plannerFieldFragment.unitPlanFieldResponses}
`;

export const getNodeUnitPlanEvidenceFeedQuery = gql`
  query getNodeUnitPlanEvidenceFeed(
    $filters: UnitPlansV2FeedFilters!
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $after: String
    $first: Int
  ) {
    node(id: $id, type: $type) {
      id
      ... on Organization {
        unitPlansV2(first: $first, after: $after, filters: $filters) {
          totalCount
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ...unitPlanItem
            }
          }
        }
      }
    }
  }
  ${unitPlanFeedFragment.unitPlanEvidenceFeed}
`;

export const getFlatUnitPlansQuery = gql`
  query getFlatUnitPlans(
    $id: ID!
    $uids: [String!]
    $first: Int
    $after: String
    $orderByDirection: OrderByDirectionEnum
    $orderBy: UnitPlanOrderByEnum
    $filters: UnitPlansV2FeedFilters!
    $exceptionalCollaboratorsFilter: ExceptionalCollaboratorUserFilter
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        id
        flatUnitPlans(
          first: $first
          after: $after
          filters: $filters
          orderByDirection: $orderByDirection
          orderBy: $orderBy
        ) {
          edges {
            node {
              id
              startDate
              endDate
              grades
              ...unitPlanItem
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${unitPlanFragment.unitPlanDetail}
`;

export const getMypRepoOrganizationDetailsQuery = gql`
  query getMypRepoOrganizationDetails($id: ID!) {
    node(id: $id, type: IB_MYP_CURRICULUM_PROGRAM) {
      id
      ... on IbMypCurriculumProgram {
        id
        repoOrganization {
          id
          subjectGroups {
            ...subjectGroupBasicDetailsItem
          }
          grades {
            ...gradeItem
          }
        }
      }
    }
  }
  ${subjectGroupFragment.subjectGroupBasicDetails}
  ${gradeFragment.basicGradeDetails}
`;

export const getCentralRepoOrgDetailsQuery = gql`
  query getCentralRepoOrgDetails(
    $id: ID!
    $subjectFilters: SubjectFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        repoOrganization {
          subjects(filters: $subjectFilters) {
            id
            name
            isArchived
            tags {
              id
              key
              value
            }
          }
          grades {
            id
            name
          }
        }
      }
    }
  }
`;

/**
 * not written in general fragment file because this is specific to publish unit plan only
 */
export const getStaffCoursesBasicDetailsQuery = gql`
  query getStaffCourses(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: CourseFilter
    $first: Int
    $after: String
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        coursesV2(
          orderBy: TITLE
          orderByDirection: ASC
          filters: $filters
          first: $first
          after: $after
        ) {
          pageInfo {
            hasNextPage
            hasPreviousPage
            endCursor
          }
          edges {
            node {
              ...courseGradeItem
              tags {
                id
                key
                value
              }
            }
          }
        }
      }
    }
  }
  ${courseFragment.courserGradesDetails}
`;

export const getTemplateFieldsQuery = gql`
  query getTemplateFields(
    $templateIds: [ID!]!
    $fieldPropertyValuesFilters: TemplateFieldPropertyValuesFilter
    $templateFieldConfigFilter: TemplateFieldConfigFilter
  ) {
    multiNode(ids: $templateIds, type: PLANNER_TEMPLATE) {
      ... on PlannerTemplate {
        id
        templateField {
          ...templateFieldConnectionItem
        }
      }
    }
  }
  ${templateFieldConnectionFragment.templateFieldConnection}
`;

export const getCentralRepoDetailsQuery = gql`
  query getCentralRepoDetails($id: ID!) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        tags {
          id
          key
          value
        }
        repoOrganization {
          presetSubjectMap
          commonCourseId
          subjects {
            id
            name
            tags {
              id
              key
              value
            }
          }
          grades {
            id
            name
          }
          curriculumPrograms {
            id
            type
            curriculum {
              id
              label
              type
            }
          }
        }
      }
    }
  }
`;

// fetches a list of names of templates associated
// with the current grade.
export const getTemplateBasicDetailsOfGradeQuery = gql`
  query getTemplateBasicDetailsOfGrade($ids: [ID!]) {
    multiNode(ids: $ids, type: GRADE) {
      id
      ... on Grade {
        id
        name
        subjects(filters: { archivalState: ALL }) {
          id
          type
        }
        plannerTemplates {
          id
          name
          type
          subType
          unitType
          isBilingual
          curriculum {
            id
            label
            type
          }
        }
      }
    }
  }
`;

export const getPlannerElementSetsOfFieldsQuery = gql`
  query getPlannerElementSetsOfFields($datasetIds: [ID!]) {
    multiNode(ids: $datasetIds, type: PLANNER_ELEMENT_SET) {
      id
      ... on PlannerElementSet {
        id
        label
        type
        config {
          id
          config
          valueType
          value
        }
        nodes {
          id
          label
          displaySequence
          subText
          type
          icon {
            id
            title
            url
            metadata
          }
          associatedParents {
            id
            ... on Grade {
              id
              name
            }
          }
          children
          code
          depth
          isLeaf
          metadata
          parent
          subType
          tags: genericTags {
            id
            label
          }
          dataSetNodeWrapper {
            properties {
              grades {
                id
                name
              }
              tags {
                id
                label
              }
            }
          }
          updatedAt
        }
      }
    }
  }
`;
export const getOrganizationUnitPlanFeedV2Query = gql`
  query getOrganizationUnitPlanFeed(
    $id: ID!
    $filters: UnitPlansV2FeedFilters!
    $showCollaborators: Boolean! = true
    $after: String
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
    $exceptionalCollaboratorsFilter: ExceptionalCollaboratorUserFilter
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        ...organizationUnitPlanFeedItem
      }
    }
  }
  ${organizationUnitPlanFeedFragment.organizationUnitPlanFeedV2}
`;

export const getFlatUnitPlanFeedV2Query = gql`
  query getFlatUnitPlanFeedV2(
    $id: ID!
    $uids: [String!]
    $first: Int
    $after: String
    $filters: UnitPlansV2FeedFilters
    $showCollaborators: Boolean!
    $showSharedWithStudentsFamily: Boolean!
    $showWeeksDuration: Boolean!
    $showAssessments: Boolean!
    $showLE: Boolean!
    $showSubjects: Boolean!
    $orderBy: UnitPlanOrderByEnum
    $groupBy: Boolean
    $orderByDirection: OrderByDirectionEnum
    $centralRepoInput: UnitCentralRepoConfigInput
    $collaboratorsFilter: CollaboratorFilters
    $exceptionalCollaboratorsFilter: ExceptionalCollaboratorUserFilter
    $classFilter: UnitPlanClassDateFilter
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseFlatFeedUnitPlanItem
      }
    }
  }
  ${courseDetailUnitPlanFeedFragment.courseFlatFeedUnitPlanV2}
`;

export const getCustomOrderingUnitPlansQuery = gql`
  query getCustomOrderedUnitPlans(
    $id: ID!
    $first: Int
    $after: String
    $orderBy: UnitPlanOrderByEnum
    $orderByDirection: OrderByDirectionEnum
  ) {
    node(id: $id, type: COURSE) {
      id
      ... on Course {
        ...courseFlatFeedUnitPlan
      }
    }
  }
  ${courseDetailUnitPlanFeedFragment.courseFlatFeedUnitPlan}
`;

export const getAllCollaboratorsQuery = gql`
  query getAllCollaborators(
    $id: ID!
    $collaboratorsFilter: CollaboratorFilters
    $first: Int
    $after: String
  ) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        collaborators: allCollaboratorsV2(
          filters: $collaboratorsFilter
          first: $first
          after: $after
        ) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              id
              firstName
              middleName
              type
              lastName
              profileImage
              email
            }
          }
        }
      }
    }
  }
`;

export const getUserCoursesQuery = gql`
  query getUserCourses(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $filters: CourseFilter
  ) {
    node(id: $id, type: $type) {
      id
      ... on Staff {
        id
        courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          id
          title
          isArchived
          isDemo
        }
      }
      ... on Student {
        id
        courses(orderBy: TITLE, orderByDirection: ASC, filters: $filters) {
          id
          title
          isArchived
          isDemo
        }
      }
    }
  }
`;

export const getUnitPlanLockAndConflictFieldsQuery = gql`
  query getUnitPlanLockAndConflictFields(
    $id: ID!
    $lockFilters: LockingInfoFiltersInput
    $syncFilters: SyncedBluePrintItemFilter
  ) {
    node(id: $id, type: UNIT_PLAN) {
      id
      ... on UnitPlan {
        id

        syncTemplateFields {
          id
          item {
            ... on PlannerField {
              id
              label
              lockingInfo(filters: $lockFilters) {
                isLocked
              }
              isSyncedWithAllTeacherCourseV2(filters: $syncFilters) {
                isConflictExists
              }
            }
          }
        }
      }
    }
  }
`;

export const getPlannerElementNodeSetIdsOfCurriculumQuery = gql`
  query getPlannerElementNodeSetIdsOfCurriculum(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
      ... on IbPypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetItem
        }
      }
    }
  }

  ${plannerElementNodeSetFragment.plannerElementNodeSet}
`;

export const getPlannerElementNodeSetsFromIdsQuery = gql`
  query getPlannerElementNodeSetsFromIds(
    $plannerElementNodeSetIds: [ID!]
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $academicYearId: ID
    $shouldSkipNodes: Boolean! = false
  ) {
    multiNode(ids: $plannerElementNodeSetIds, type: PLANNER_ELEMENT_NODE_SET) {
      ... on PlannerElementNodeSet {
        ...plannerElementNodeSetWithNodesItem
      }
    }
  }
  ${plannerElementNodeSetFragment.plannerElementNodeSetWithNodes}
`;

export const getPlannerElementNodeSetsFromCurriculumQuery = gql`
  query getPlannerElementNodeSetsFromCurriculum(
    $id: ID!
    $type: ENTITY_TYPE_ENUM!
    $plannerElementNodeSetFilters: PlannerElementNodeSetFilters
    $plannerElementNodeFilters: PlannerElementNodeFilters
    $academicYearId: ID
    $shouldSkipNodes: Boolean! = false
  ) {
    node(id: $id, type: $type) {
      id
      ... on IbMypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetWithOptionalNodesItem
        }
      }
      ... on UBDCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetWithOptionalNodesItem
        }
      }
      ... on IbDpCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetWithOptionalNodesItem
        }
      }
      ... on IbPypCurriculumProgram {
        id
        plannerElementNodeSets(filters: $plannerElementNodeSetFilters) {
          ...plannerElementNodeSetWithOptionalNodesItem
        }
      }
    }
  }

  ${plannerElementNodeSetFragment.plannerElementNodeSetWithOptionalNodes}
`;

export const getGradeDetailsQuery = gql`
  query getGradeDetails($ids: [ID!]) {
    multiNode(ids: $ids, type: GRADE) {
      id
      ... on Grade {
        ...gradeItem
      }
    }
  }
  ${unitPlanGradeFragment.gradeDetails}
`;
